import type { CreateTask, EditTask } from '../../types/server/models/task'
import style from '../../assets/style.json'
import Swal from 'sweetalert2'
import { DragDropContext, Draggable, DraggableLocation, Droppable, DropResult } from 'react-beautiful-dnd'
import { CreateBlueprint, UpdateBlueprint, useCompanyBlueprints } from '../../services/blueprint.service'
import { CreateOrEditBlueprint } from '../../types/server/endpoints/blueprints'
import { IntervalPicker } from '../../components/IntervalPicker'
import { useReadCompany } from '../../services/company-service'
import { CirclePlusIcon } from '../../assets/CirclePlusIcon'
import { useNavigate, useParams } from 'react-router-dom'
import { DownloadIcon } from '../../assets/DownloadIcon'
import { UploadFiles } from '../../utils/hooks/useFiles'
import { TrashIcon } from '../../assets/TrashIcon'
import { getMimeType } from '../../services/mime'
import { useTranslation } from 'react-i18next'
import { appConfig } from '../../configs/app'
import { useEffect, useState } from 'react'
import { saveAs } from 'file-saver'
import {
	Accordion,
	AccordionButton,
	AccordionItem,
	AccordionPanel,
	Box,
	Button,
	Container,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Text,
	Textarea,
	AccordionIcon,
	useMediaQuery,
} from '@chakra-ui/react'

const reorder = (list: any[], startIndex: number, endIndex: number) => {
	const result = Array.from(list)
	const [removed] = result.splice(startIndex, 1)
	result.splice(endIndex, 0, removed)
	return result
}

const move = (
	source: any[],
	destination: any[],
	droppableSource: DraggableLocation,
	droppableDestination: DraggableLocation
) => {
	const sourceClone = Array.from(source)
	const destClone = Array.from(destination)
	const [removed] = sourceClone.splice(droppableSource.index, 1)

	destClone.splice(droppableDestination.index, 0, removed)

	return {
		[droppableSource.droppableId]: sourceClone,
		[droppableDestination.droppableId]: destClone,
	}
}

const EditBlueprint = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()

	const [items, setItems] = useState<{ [key: string]: { items: string[] } }>({})
	const [isPc] = useMediaQuery('(min-width: 1300px)')

	console.log({ items })

	const [taskId, setTaskd] = useState<string>()

	const { companyId, blueprintId } = useParams<{ companyId: string; blueprintId?: string }>()

	const [isSubmitting, setIsSubmitting] = useState(false)
	const [isUploadingFile, setIsUploadingFile] = useState(false)

	const { data: company } = useReadCompany(companyId)
	const { data: companyBlueprints } = useCompanyBlueprints(company?._id)

	const [blueprint, setBlueprint] = useState<CreateOrEditBlueprint>({
		companyId: company?._id! || companyId!,
		roleId: '',
		tasks: [],
	})

	const selectedBlueprint = blueprintId ? companyBlueprints.find((b) => b._id === blueprintId) : undefined

	useEffect(() => {
		// Set the initial state of the items
		setItems(
			Object.assign(
				{},
				...(company?.exposureCategories
					? company.exposureCategories.map((category) => ({
							[category.id!]: { items: [] },
					  }))
					: [])
			)
		)

		if (selectedBlueprint) {
			// Set the initial state of the blueprint
			setBlueprint({
				companyId:
					typeof selectedBlueprint.company === 'string'
						? selectedBlueprint.company
						: selectedBlueprint.company._id,
				roleId:
					typeof selectedBlueprint?.role === 'string'
						? selectedBlueprint.role
						: selectedBlueprint?.role._id || '',
				tasks: selectedBlueprint?.tasks || [],
			})

			setItems(
				company?.exposureCategories.reduce(
					(acc, category) => {
						acc[category.id as string] = {
							items:
								selectedBlueprint.tasks
									?.filter((item) => item.riskId === category.id)
									.map((t) => t._id) || [],
						}

						return acc
					},
					{} as {
						[key: string]: {
							items: string[]
						}
					}
				) || {}
			)
		}
	}, [companyId, blueprintId, company, selectedBlueprint])

	const deleteFile = async (path: string, taskId: string) => {
		const task = blueprint.tasks.find((task: CreateTask | EditTask) =>
			'tempId' in task ? task.tempId === taskId : '_id' in task ? task._id === taskId : null
		)
		if (!task) return

		const file = task.adminFiles.find((file) => file.path === path)
		if (!file) return

		const result = await Swal.fire({
			title: t('addTasksForm.deleteFile') + `"${file.name}"?`,
			text: t('addTasksForm.deleteWarning'),
			showCancelButton: true,
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			cancelButtonText: t('addTasksForm.cancel'),
			confirmButtonText: t('addTasksForm.delete'),
		})

		if (result.isConfirmed) {
			// Filter out the file from the task
			const newTask = {
				...task,
				adminFiles: task.adminFiles.filter((f) => f.path !== path),
			}

			// Update the blueprint with the new task (match by tempId or _id)
			const newBlueprint = {
				...blueprint,
				tasks: blueprint.tasks.map((t) => {
					if ('tempId' in t && 'tempId' in task && t.tempId === task.tempId) return newTask
					if ('_id' in t && '_id' in task && t._id === task._id) return newTask
					return t
				}),
			}

			setBlueprint(newBlueprint)
		}
	}

	const onDragEnd = (result: DropResult) => {
		const { source, destination } = result

		// dropped outside the list
		if (!destination) {
			return
		}

		if (source.droppableId === destination.droppableId) {
			const newItems = reorder(items[source.droppableId].items, source.index, destination.index)
			setItems({ ...items, [source.droppableId]: { items: newItems } })
		} else {
			const result = move(
				items[source.droppableId].items,
				items[destination.droppableId].items,
				source,
				destination
			)

			setItems({
				...items,
				[source.droppableId]: { items: result[source.droppableId] },
				[destination.droppableId]: { items: result[destination.droppableId] },
			})
		}
	}

	const isValid = () => {
		const isHaveItems = Object.keys(items).some((k) => {
			return items[k].items.map((i) => i).length > 0
		})

		// const haveEmpties = Object.keys(items).some((k) => items[k].items.some((item) => !item.text))
		const haveEmpties = blueprint.tasks.some((task) => task.text === '' || task.text === undefined)

		return !haveEmpties && isHaveItems
	}

	const addItem = (section: string) => {
		const newItems = items[section].items
		const newItem: CreateTask = {
			tempId: (Math.random() + 1).toString(36).substring(7),
			riskId: section,
			text: '',
			startDate: new Date(),
			endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
			interval: 'MONTH',
			intervalAmount: 1,
			adminFiles: [],
			// Current day of month
			// dayOfMonth: new Date().getDate(),
			dayOfMonth: 20,
		}

		newItems.push(newItem.tempId!)

		setItems({ ...items, [section]: { items: newItems } })

		setBlueprint({
			...blueprint,
			tasks: [...blueprint.tasks, newItem],
		})
	}

	const removeItem = (taskId: string) =>
		setBlueprint({
			...blueprint,
			tasks: blueprint.tasks.filter((item) => {
				if ('tempId' in item) {
					return item.tempId !== taskId
				} else if ('_id' in item) {
					return item._id !== taskId
				}

				return false
			}),
		})

	const onChangeText = (taskId: string, value: string) => {
		// Find the blueprint task
		const task = blueprint.tasks.find((task: CreateTask | EditTask) =>
			'tempId' in task ? task.tempId === taskId : '_id' in task ? task._id === taskId : null
		)
		if (!task) return

		console.log({ foundTask: task })

		// Update the task text
		const newTask = {
			...task,
			text: value,
		}

		// Update the blueprint with the new task
		const newBlueprint = {
			...blueprint,
			// tasks: blueprint.tasks.map((t) => (t.tempId === task.tempId ? newTask : t)), //
			tasks: blueprint.tasks.map((t) => {
				if (t.tempId === taskId) {
					return newTask
				} else if ('_id' in t && t._id === taskId) {
					return newTask
				}

				return t
			}),
		}

		setBlueprint(newBlueprint)
	}

	const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (isSubmitting) return
		if (!e.target?.files) return
		if (!taskId) return

		setIsUploadingFile(true)

		const fileArr = e.target?.files ? Array.from(e.target.files) : []

		const uploadedFiles = await UploadFiles(fileArr)

		// Add it to the task progress under the taskId
		const task = blueprint.tasks.find((task: CreateTask | EditTask) =>
			'tempId' in task ? task.tempId : '_id' in task ? task._id === taskId : null
		)

		if (task) {
			const newBlueprint = {
				...blueprint,
				tasks: blueprint.tasks.map((t) => {
					if (t.tempId === taskId || ('_id' in t && t._id === taskId)) {
						return {
							...t,
							adminFiles: [...(t.adminFiles || []), ...uploadedFiles],
						}
					}
					return t
				}),
			}

			setBlueprint(newBlueprint)
		}

		e.target.value = ''
		e.target.files = null

		setIsUploadingFile(false)
	}

	const [isDownloading, setIsDownloading] = useState(false)

	const handleDownloadFile = async (fileName: string, path: string) => {
		if (isDownloading) return
		setIsDownloading(true)

		try {
			let response = await fetch(`${path}&fileName=${fileName}`)
			let data = await response.blob()

			let splittedFileName = fileName.split('.')
			let extension = splittedFileName.at(splittedFileName.length - 1)

			let fixedData = new Blob([data], { type: getMimeType(extension ?? '') })
			saveAs(fixedData, fileName)
		} finally {
			setIsDownloading(false)
		}
	}

	const uploadFile = async (id: string) => {
		setTaskd(id)
		const input = document.getElementById('fileInput')

		input?.click()
	}

	const handleSubmit = async () => {
		if (!company) return
		if (isSubmitting) return

		setIsSubmitting(true)

		!!blueprintId
			? await UpdateBlueprint(blueprintId, {
					roleId: blueprint.roleId,
					tasks: blueprint.tasks
						// Filter out tasks that are not found in the "items" state
						.filter((t) => {
							const found = Object.keys(items).find((k) =>
								items[k].items.includes(t.tempId || ('_id' in t && (t._id as any)))
							)

							return !!found
						})
						.map((t) => ({
							...t,
							// Get the riskId from the items object
							riskId: Object.keys(items).find((k) =>
								items[k].items.includes(t.tempId || ('_id' in t && (t._id as any)))
							),
						})) as EditTask[],
			  })
			: await CreateBlueprint(blueprint)

		setIsSubmitting(false)
		navigate(`/admin/blueprints?company_id=${companyId}`)
		Swal.fire(
			t('addTasksForm.success'),
			!!blueprintId ? t('addTasksForm.taskListEdited') : t('addTasksForm.taskListCreated'),
			'success'
		)
	}

	return (
		<Container
			position='absolute'
			top={'90px'}
			maxW='full'
			py={20}
			centerContent
		>
			<Text
				color={'white'}
				dir='rtl'
				marginBottom={'77px'}
				fontWeight={500}
				fontSize={32}
			>
				{!!blueprintId ? t('addTasksForm.editList') : t('addTasksForm.addList')}
			</Text>
			<Box
				display={'flex'}
				flexDirection='column'
				alignItems={'center'}
				maxW={style['pc-view']}
				width='full'
				boxShadow='0px 0px 25px rgba(62, 62, 62, 0.08)'
				rounded='10px'
				bg='white'
				padding='21px'
			>
				{isPc && (
					<Box
						width={'100%'}
						display='flex'
						flexDirection='row-reverse'
					>
						<Menu closeOnSelect={false}>
							<MenuButton
								rounded={'100px'}
								color='white'
								bg={'#3793AC'}
								as={Button}
								minWidth='188px'
								mb={'10px'}
							>
								{company?.roles?.find((role) => role._id === blueprint.roleId)
									? `${company?.roles?.find((role) => role._id === blueprint.roleId)?.name}`
									: t('addTasksForm.selectJob')}
							</MenuButton>
							<MenuList
								overflowY='auto'
								maxH={'300px'}
							>
								<Text
									px={3}
									fontWeight='bold'
									mb={2}
								>
									{t('addTasksForm.roles')}
								</Text>
								{company?.roles
									// Filter out roles that exists in any of the current blueprints
									.filter((r) => {
										return !companyBlueprints.some((b) => b.role === r._id && b._id !== blueprintId)
									})
									.map((r) => (
										<MenuItem
											onClick={() => {
												setBlueprint({ ...blueprint, roleId: r._id })
											}}
											_hover={{
												bg: '#F5F8FA',
											}}
											key={r._id}
											display='flex'
											justifyContent={'space-between'}
										>
											<Text>{r.name}</Text>
											{r._id === blueprint.roleId && <Text color={'#3FB2D2'}>✓</Text>}
										</MenuItem>
									))}
							</MenuList>
						</Menu>
					</Box>
				)}

				<Text
					color={'#475154'}
					fontSize={'22px'}
					fontWeight='400'
				>
					{company?.name}
				</Text>

				{!isPc && (
					<Box
						width={'100%'}
						display='flex'
						justifyContent={'center'}
						alignItems={'center'}
					>
						<Menu closeOnSelect={false}>
							<MenuButton
								rounded={'100px'}
								color='white'
								bg={'#3793AC'}
								as={Button}
								minWidth='188px'
								mb={'10px'}
								mt={'10px'}
							>
								{company?.roles?.find((role) => role._id === blueprint.roleId)
									? `${company?.roles?.find((role) => role._id === blueprint.roleId)?.name}`
									: t('addTasksForm.selectJob')}
							</MenuButton>
							<MenuList
								overflowY='auto'
								maxH={'300px'}
							>
								<Text
									px={3}
									fontWeight='bold'
									mb={2}
								>
									{t('addTasksForm.roles')}
								</Text>
								{company?.roles?.map((r) => (
									<MenuItem
										onClick={() => {
											setBlueprint({ ...blueprint, roleId: r._id })
										}}
										_hover={{
											bg: '#F5F8FA',
										}}
										key={r._id}
										display='flex'
										justifyContent={'space-between'}
									>
										<Text>{r.name}</Text>
										{r._id === blueprint.roleId && <Text color={'#3FB2D2'}>✓</Text>}
									</MenuItem>
								))}
							</MenuList>
						</Menu>
					</Box>
				)}

				<Text
					fontWeight='400'
					lineHeight={'60px'}
					fontSize={18}
					color='#A9A9A9'
					textAlign={'start'}
					width='100%'
				>
					{t('addTasksForm.tasks')}
				</Text>

				<DragDropContext onDragEnd={onDragEnd}>
					<Accordion
						display={'flex'}
						flexDirection='column'
						gap={'21px'}
						width={'100%'}
						allowMultiple
					>
						{!Object.keys(items)?.length && (
							<>
								<Text opacity={0.7}>{t('addTasksForm.noRisks')}!</Text>
								<Button
									mt={5}
									onClick={() => navigate(`/admin/company/${company?.id}`)}
								>
									{t('addTasksForm.addRisk')}
								</Button>
							</>
						)}
						{Object.keys(items)?.map((section, sectionIndex) => (
							<AccordionItem
								w={'full'}
								margin={'0px'}
								borderTop='0'
								borderBottom='0'
								background='#F5F8FA'
								rounded='10px'
								key={section}
							>
								<AccordionButton
									justifyContent='space-between'
									paddingBlock={'20px'}
									_focus={{ outline: 0 }}
									rounded={'10px'}
								>
									<Text
										fontSize={22}
										fontWeight='600'
									>
										{/* {categories.filter((x) => x.id === section)[0]?.name} */}
										{company?.exposureCategories.filter((x) => x.id === section)[0]?.name} (
										{items[section].items.length})
									</Text>
									<AccordionIcon />
								</AccordionButton>
								<AccordionPanel
									rounded={'10px'}
									padding={'21px'}
								>
									<Droppable droppableId={section}>
										{(provided) => (
											<Box
												ref={provided.innerRef}
												{...provided.droppableProps}
												w='100%'
											>
												{items[section].items.map((i, index) => {
													const item = blueprint.tasks.find((task: CreateTask | EditTask) =>
														'tempId' in task
															? task.tempId === i
															: '_id' in task
															? task._id === i
															: null
													)

													console.log({ item, i, tasks: blueprint.tasks })

													if (!item) return null

													const itemId =
														'tempId' in item
															? item.tempId
															: '_id' in item
															? (item._id as string)
															: null

													console.log({ itemId, i })
													if (!itemId) return null

													return (
														<Draggable
															key={sectionIndex + '-' + index}
															draggableId={sectionIndex + '-' + index}
															index={index}
														>
															{(provided) => (
																<Box>
																	<Box
																		pos='relative'
																		w='100%'
																		py={1}
																		display={'flex'}
																		gap='23px'
																		flexDirection={isPc ? 'row' : 'column'}
																		borderBottom={
																			isPc ? 'unset' : '1.5px solid #DFDFDF'
																		}
																		ref={provided.innerRef}
																		{...provided.draggableProps}
																		{...provided.dragHandleProps}
																	>
																		<Box flexGrow={1}>
																			<Text
																				color={'#aaaaaa'}
																				fontSize={'14px'}
																				fontWeight='400'
																				marginBottom='5px'
																			>
																				{t('addTasksForm.taskName')}
																			</Text>
																			<Textarea
																				height={'54px'}
																				bg='#fff'
																				border='1.2521px solid #E2E2E2'
																				borderRadius={'10px'}
																				fontStyle='18px'
																				paddingBlock={'15px'}
																				paddingInline='20px'
																				minH='54px'
																				value={item?.text}
																				onChange={(e) =>
																					onChangeText(itemId, e.target.value)
																				}
																				placeholder={t(
																					'addTasksForm.taskDescription'
																				)}
																			/>
																		</Box>
																		<Box
																			display='flex'
																			flexDirection='column-reverse'
																			paddingBottom='10px'
																		>
																			<Button
																				onClick={() => uploadFile(itemId)}
																				minW={'188px'}
																				textAlign='center'
																				fontSize='18px'
																				fontWeight={700}
																				rounded={'100px'}
																				borderWidth={'1.5px'}
																				borderColor={style['primary-color']}
																				bg={'#fff'}
																				color={style['primary-color']}
																				isLoading={isUploadingFile}
																			>
																				<Box
																					gap={'17px'}
																					display={'flex'}
																					alignItems={'center'}
																				>
																					<Text>
																						{t('addTasksForm.addFile')}
																					</Text>
																					<CirclePlusIcon />
																				</Box>
																			</Button>
																		</Box>

																		<Box>
																			<Text
																				color={'#aaaaaa'}
																				fontSize={'14px'}
																				fontWeight='400'
																				marginBottom='5px'
																			>
																				{t('addTasksForm.taskDetails')}
																			</Text>
																			{item && (
																				<IntervalPicker
																					interval={{
																						...item,
																						startDate: new Date(
																							item.startDate
																						),
																						endDate: new Date(item.endDate),
																					}}
																					changeInterval={(newInterval) => {
																						// const newItems =
																						// 	items[section].items

																						// newItems[index] = {
																						// 	...newItems[index],
																						// 	...newInterval,
																						// }

																						// setItems({
																						// 	...items,
																						// 	[section]: { items: newItems },
																						// })

																						// Find the blueprint task
																						const task =
																							blueprint.tasks.find(
																								(
																									task:
																										| CreateTask
																										| EditTask
																								) =>
																									'tempId' in task
																										? task.tempId ===
																										  itemId
																										: '_id' in task
																										? task._id ===
																										  itemId
																										: null
																							)

																						if (!task) return

																						// Update the task
																						const newTask = {
																							...task,
																							...newInterval,
																						}

																						// Update the blueprint with the new task
																						const newBlueprint = {
																							...blueprint,
																							tasks: blueprint.tasks.map(
																								(t) =>
																									t.tempId ===
																									task.tempId
																										? newTask
																										: t
																							),
																						}

																						setBlueprint(newBlueprint)
																					}}
																				/>
																			)}
																		</Box>
																		<Button
																			mt={isPc ? '30px' : '-10px'}
																			bg='transparent'
																			color={'#3FB2D2'}
																			onClick={() => removeItem(itemId)}
																			display='flex'
																			gap='8px'
																			justifyContent={
																				isPc ? 'center' : 'flex-end'
																			}
																		>
																			{isPc ? '' : t('addTasksForm.delete')}
																			<TrashIcon />
																		</Button>
																	</Box>
																	<Box>
																		{(item.adminFiles ?? []).map((file) => {
																			const { name, path } = file

																			return (
																				<Box
																					display={'flex'}
																					gap={'20px'}
																					alignItems={'center'}
																					key={file.path}
																				>
																					<Text
																						fontSize={'18px'}
																						lineHeight='60px'
																						color={'#A9A9A9'}
																						fontWeight={400}
																					>
																						{name ??
																							t('addTasksForm.file') +
																								name}
																						{/* {file.date
																							? ` - ${UtilSrvice.timestampToDDMMYYYY(
																									file.date.getTime()
																							  )}`
																							: ''} */}
																					</Text>
																					<Button
																						display='flex'
																						padding={'15px'}
																						alignItems='center'
																						justifyContent='center'
																						gap={'20px'}
																						color={style['primary-color']}
																						width='max-content'
																						height='max-content'
																						bg={'#fff'}
																						isLoading={isDownloading}
																						isDisabled={isDownloading}
																						boxShadow='0px 0px 20px rgba(62, 62, 62, 0.11)'
																						onClick={() =>
																							handleDownloadFile(
																								name,
																								`${
																									appConfig.apiUrl
																								}/file?path=${path}&token=${localStorage.getItem(
																									'token'
																								)}`
																							)
																						}
																					>
																						<DownloadIcon />
																						<Text color='#2D2F32'>
																							{t('addTasksForm.download')}
																						</Text>
																					</Button>

																					<Button
																						display='flex'
																						padding={'15px'}
																						alignItems='center'
																						justifyContent='center'
																						gap={'20px'}
																						color={style['primary-color']}
																						width='max-content'
																						height='max-content'
																						bg={'#fff'}
																						boxShadow='0px 0px 20px rgba(62, 62, 62, 0.11)'
																						onClick={() =>
																							deleteFile(
																								file.path,
																								itemId
																							)
																						}
																					>
																						<TrashIcon />
																						<Text color='#2D2F32'>
																							{t('addTasksForm.delete')}
																						</Text>
																					</Button>
																				</Box>
																			)
																		})}
																	</Box>
																</Box>
															)}
														</Draggable>
													)
												})}

												{provided.placeholder}
											</Box>
										)}
									</Droppable>
									{items[section].items.length === 0 ? (
										<Button
											width={'full'}
											onClick={() => addItem(section)}
											alignSelf='center'
											color={'#3FB2D2'}
											bg='transparent'
											height='78px'
											display={'flex'}
											gap='16px'
											border='1.2521px dashed #3FB2D2'
										>
											<Text>{t('addTasksForm.addTask')}</Text>
											<CirclePlusIcon />
										</Button>
									) : (
										<Box
											display='flex'
											flexDirection='row-reverse'
										>
											<Button
												onClick={() => addItem(section)}
												alignSelf='center'
												color={'#3FB2D2'}
												bg='transparent'
												marginTop={'20px'}
												display={'flex'}
												gap='16px'
											>
												<Text>{t('addTasksForm.addTask')}</Text>
												<CirclePlusIcon />
											</Button>
										</Box>
									)}
								</AccordionPanel>
							</AccordionItem>
						))}
					</Accordion>
				</DragDropContext>
				<Box
					marginTop={'44px'}
					display={'flex'}
					flexDirection='row-reverse'
					width={'full'}
				>
					<Button
						color={'white'}
						minW={isPc ? '200px' : 'full'}
						height='60px'
						textAlign='center'
						border='3px solid #FFFFFF'
						background={'#3FB2D2'}
						fontSize='18px'
						disabled={!blueprint.roleId || !isValid() || isSubmitting}
						fontWeight={700}
						rounded={'100px'}
						filter='drop-shadow(0px 4px 30px rgba(63, 178, 210, 0.44))'
						onClick={async () => {
							if (isSubmitting) return
							handleSubmit()
						}}
						isLoading={isSubmitting}
					>
						{t('addTasksForm.save')}
					</Button>
				</Box>
			</Box>
			<input
				type='file'
				id='fileInput'
				style={{ visibility: 'hidden', width: 0, height: 0 }}
				onChange={(e) => handleFileInput(e)}
				multiple
			/>
		</Container>
	)
}

export default EditBlueprint
